import { sendAnalytics } from '@audio-network/analytics'
import {
	Album,
	Blog,
	Cart,
	ChevronDown,
	ChevronUp,
	Composer,
	Delete,
	Download,
	Folder,
	Mix,
	MusicalNote,
	Notes,
	NotesFilled,
	Related,
	Star,
	StarFilled,
	Stems,
} from '@audio-network/componentlibrary/icons'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useId } from 'react'

import { useDownloadTrack } from '@/components/shared/DownloadOptionsModal'
import { useActiveProject } from '@/hooks/useActiveProject'
import { addToCart } from '@/services/cartService'
import useFavouritesStore from '@/stores/favouritesStore'
import sessionStore from '@/stores/sessionStore'
import getToggleFromCookie from '@/utils/cookies/getToggleFromCookie'
import { hasStemsAccess } from '@/utils/hasStemsAccess'
import { trackHasStems } from '@/utils/trackHasStems'
import { getAlbumUrl, getComposerUrl, getTrackUrl } from '@/utils/urls'
import { redirectToHref } from '@/utils/window'

import { handleLicenseClick } from './License/License.utils'
import {
	CART_ACTION,
	DOWNLOAD_MP3_ACTION,
	FAVOURITES_ACTION,
	LICENSE_ACTION,
	NOTES_ACTION,
	PROJECTS_ACTION,
	SIMILAR_TRACKS_ACTION,
} from './TrackActions.constants'
import { MenuItemProps, TrackActionsProps } from './TrackActions.types'

export const useTrackActions = ({
	track,
	source,
}: Pick<TrackActionsProps, 'track' | 'source'>): {
	trackOptions: MenuItemProps[]
	userActions: MenuItemProps[]
	projectActions: MenuItemProps[]
	canViewTrackCatalogue: boolean
	addToProjectModalId: string
	handleAddToProjectOpen: () => void
} => {
	const { locale } = useRouter()
	const { t } = useTranslation(['trackrow'])
	const user = sessionStore((state) => state.session?.user)
	const favouriteTracks = useFavouritesStore((state) => state.favouriteTracks)
	const toggleFavourite = useFavouritesStore((state) => state.toggleFavourite)
	const { downloadTrack } = useDownloadTrack(source, track.title, track.id)
	// Strip React useId colons — invoker commandfor / attribute selectors break on `:`.
	const addToProjectModalId = `track-row-add-to-project-modal-${useId().replaceAll(
		':',
		''
	)}`

	const {
		isEditable,
		onChangeNotes,
		onTracksDelete,
		project,
		reorderTracks,
		tracks: projectTracks,
	} = useActiveProject()

	const canViewTrackCatalogue =
		!user || user.viewableCatalogues.includes(track.album.catalogue.id)
	const isFavourite = !!favouriteTracks?.some((trackId) => trackId === track.id)
	const trackUrl = getTrackUrl(track)

	const handleSimilarTracksClick = (): void => {
		sendAnalytics({
			noun: 'Similar Tracks',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackId: track.id,
				TrackTitle: track.title,
				AlbumNo: track.album.number,
				TrackSource: source,
				Genre: track.album.genre,
			},
		})
	}

	const handleAddToProjectOpen = (): void => {
		sendAnalytics({
			noun: 'Add to Project',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Source: source,
			},
		})
	}

	const handleAddToCart = async (): Promise<void> => {
		const updatedCart = await addToCart(track.id, user.id)
		if (updatedCart) {
			sendAnalytics({
				noun: 'Add to Cart',
				pastTenseVerb: 'Clicked',
				eventParameters: {
					TrackId: track.id,
					TrackTitle: track.title,
					AlbumNo: track.album.number,
					CartCount: updatedCart?.itemsCount,
					TrackSource: source,
					Genre: track.album.genre,
					Catalogue: track.album.catalogue.name,
				},
			})
		}
		redirectToHref('/cart')
	}

	const trackOptionsConfig = [
		{
			id: SIMILAR_TRACKS_ACTION,
			Icon: <Related />,
			title: t('track-actions.similar-tracks'),
			href: `${trackUrl}?initialTab=similar+tracks`,
			include: !!user,
			onClick: handleSimilarTracksClick,
		},
		{
			id: 'TrackDetails',
			Icon: <MusicalNote />,
			title: t('track-actions.go-to-track-details'),
			href: trackUrl,
			include: true,
		},
		{
			id: 'Album',
			Icon: <Album />,
			title: `${t('album')}: ${track.album.name}`,
			href: getAlbumUrl(track.album),
			include: !!track.album,
		},
		...track.composers.map((composer) => ({
			id: `Composer`,
			title: `${composer.name} (${composer.collectionSociety})`,
			Icon: <Composer />,
			href: getComposerUrl(composer),
			include: true,
		})),
		{
			id: 'Mixes',
			Icon: <Mix />,
			title: `${track.mixCount} mixes available`,
			href: trackUrl,
			include: track?.mixCount >= 2,
		},
		{
			id: 'Stems',
			Icon: <Stems />,
			title: t('track-actions.stems-available'),
			href: `${trackUrl}?initialTab=stems`,
			include: hasStemsAccess(user) && trackHasStems(track),
		},
	]

	const userActionsConfig = [
		{
			id: FAVOURITES_ACTION,
			Icon: isFavourite ? <StarFilled color="yellow" /> : <Star />,
			title: t('track-actions.favourite-track'),
			'aria-pressed': isFavourite,
			onClick: () => toggleFavourite(track, source),
			include: !!user?.id && canViewTrackCatalogue,
		},
		{
			id: PROJECTS_ACTION,
			Icon: <Folder />,
			title: t('track-actions.add-to-project'),
			onClick: handleAddToProjectOpen,
			command: 'show-modal',
			commandfor: addToProjectModalId,
			include: !!user?.id && canViewTrackCatalogue,
		},
		{
			id: 'Download-wav',
			Icon: <Download />,
			title: t('track-actions.download-row-option-wav'),
			onClick: () => {
				void downloadTrack(track, 'wav')
			},
			include: !!user?.organisation.canDownload && canViewTrackCatalogue,
		},
		{
			id: 'Download-aiff',
			Icon: <Download />,
			title: t('track-actions.download-row-option-aiff'),
			onClick: () => {
				void downloadTrack(track, 'aiff')
			},
			include:
				!!user?.organisation.canDownload &&
				canViewTrackCatalogue &&
				getToggleFromCookie('AIFFsInDownloadModal'),
		},
		{
			id: DOWNLOAD_MP3_ACTION,
			Icon: <Download />,
			title: t('track-actions.download-row-option-mp3'),
			onClick: () => {
				void downloadTrack(track, 'mp3')
			},
			include: !!user?.organisation.canDownload && canViewTrackCatalogue,
		},
		{
			id: CART_ACTION,
			Icon: <Cart />,
			title: t('track-actions.add-to-cart'),
			onClick: handleAddToCart,
			include: !!user?.id && canViewTrackCatalogue,
		},
		{
			id: LICENSE_ACTION,
			Icon: <Blog />,
			title: t('track-actions.license-more-options'),
			onClick: () => handleLicenseClick(track.id, source),
			include: !user && locale !== 'ja-JP',
		},
	]

	const trackHasNotes =
		project?.Tracks?.find((t) => t.Id === track.id)?.Notes?.length > 0

	const oldPosition = projectTracks?.findIndex(
		(projectTrack) => projectTrack.id === track.id
	)

	const projectActionsConfig = [
		{
			id: NOTES_ACTION,
			Icon: trackHasNotes ? <NotesFilled /> : <Notes />,
			title: t('track-actions.notes'),
			include: (!!onChangeNotes && isEditable) || trackHasNotes,
		},
		{
			id: 'MoveToTop',
			Icon: <ChevronUp />,
			title: t('track-actions.move-to-top'),
			onClick: () => reorderTracks(oldPosition, 0, 'To Top'),
			include: !!reorderTracks && isEditable,
		},
		{
			id: 'MoveToBottom',
			Icon: <ChevronDown />,
			title: t('track-actions.move-to-bottom'),
			onClick: () =>
				reorderTracks(oldPosition, projectTracks.length - 1, 'To Bottom'),
			include: !!reorderTracks && isEditable,
		},
		{
			id: 'Delete',
			Icon: <Delete />,
			title: t('track-actions.delete'),
			onClick: () => {
				sendAnalytics({
					noun: 'Remove from Project',
					pastTenseVerb: 'Clicked',
					eventParameters: {
						TrackId: track.id,
					},
				})
				onTracksDelete([track])
			},
			include: !!onTracksDelete && isEditable,
		},
	]

	return {
		trackOptions: trackOptionsConfig
			.filter((option) => option.include)
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			.map(({ include, ...option }) => option),
		userActions: userActionsConfig
			.filter((option) => option.include)
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			.map(({ include, ...option }) => option),
		projectActions: projectActionsConfig
			.filter((option) => option.include)
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			.map(({ include, ...option }) => option),
		canViewTrackCatalogue,
		addToProjectModalId,
		handleAddToProjectOpen,
	}
}
